import React, { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare, faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import 'bulma/css/bulma.min.css';
import { ValidatorType, ValidatorStep, validatorDisplayOrder, validatorInfo, validatorStepToDescription } from '../validation';


interface ValidationCellModalContentProps {
  value: ValidatorStep | null;
  onValueChange: (input: ValidatorStep | null) => void;
}


enum OperatorType {
  And = "And",
  Or = "Or"
}


interface ValidationStepTableEntry {
  indent: number;
  validatorStep: ValidatorStep;
}


const ValidationCellModalContent: React.FC<ValidationCellModalContentProps> = (props) => {

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [typeValue, setTypeValue] = useState<ValidatorType>(ValidatorType.Grouping);
  const [operatorValue, setOperatorValue] = useState<OperatorType>(OperatorType.And);
  const [argumentsValue, setArgumentsValue] = useState<string[]>([]);
  const [notValue, setNotValue] = useState<boolean>(false);

  const validatorStepTableEntries: ValidationStepTableEntry[] = [];

  function recurseValidatorSteps(validatorStep: ValidatorStep, indent: number) {
    validatorStepTableEntries.push({ indent, validatorStep });
    if (validatorStep.and) {
      for (const subStep of validatorStep.and) {
        recurseValidatorSteps(subStep, indent+1);
      }
    }
    if (validatorStep.or) {
      for (const subStep of validatorStep.or) {
        recurseValidatorSteps(subStep, indent+1);
      }
    }
  }

  if (props.value) { recurseValidatorSteps(props.value, 0); }

  
  // Turn the array of steps back into a tree
  function recomposeSteps(steps: ValidationStepTableEntry[]): ValidatorStep | null {
    if (steps.length == 0) { return null; }
    const stack: ValidatorStep[] = [];
    for (const entry of steps) {
      const newStep: ValidatorStep = { };
      if (entry.validatorStep.not) { newStep.not = true; }
      if (entry.validatorStep.arguments && entry.validatorStep.arguments.length > 0) { newStep.arguments = [ ...entry.validatorStep.arguments ]; }
      if (entry.validatorStep.type != ValidatorType.Grouping) {
        newStep.type = entry.validatorStep.type;
      }
      if (Object.hasOwn(entry.validatorStep, "and")) {
        newStep.and = [];
      }
      if (Object.hasOwn(entry.validatorStep, "or")) {
        newStep.or = [];
      }
      if (stack.length == 0) {
        stack.push( newStep );
        continue;
      }
      const parent = stack[entry.indent-1];
      if (Object.hasOwn(parent, "and")) {
        parent.and!.push(newStep);
      } else if (Object.hasOwn(parent, "or")) {
        parent.or!.push(newStep);
      }
      stack[entry.indent] = newStep;
    }
    return stack[0];
  }


  function selectIndex(index: number) {
    setSelectedIndex(index);
    const stepEntry = validatorStepTableEntries[index-1];
    if (stepEntry) {
      setTypeValue(stepEntry.validatorStep.type || ValidatorType.Grouping);
      if (Object.hasOwn(stepEntry.validatorStep, "or")) {
        setOperatorValue(OperatorType.Or);
      } else {
        setOperatorValue(OperatorType.And);
      }
      setArgumentsValue([...(stepEntry.validatorStep.arguments || [])]);
      setNotValue(stepEntry.validatorStep.not || false);
    } else {
      setTypeValue(ValidatorType.Grouping);
      setOperatorValue(OperatorType.And);
      setArgumentsValue([]);
      setNotValue(false);
    }
  }


  function typeOnChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value as ValidatorType;
    setTypeValue(value);
    if (selectedIndex == 0) { return }
    const stepEntry = validatorStepTableEntries[selectedIndex-1];
    if (!stepEntry) { return }
    stepEntry.validatorStep.type = value;
    const newArray = [...(stepEntry.validatorStep.arguments || [])];
    const argumentsLength = validatorInfo[value].arguments;
    while (newArray.length < argumentsLength) {
      newArray.push("");
    }
    if (newArray.length > argumentsLength) {
      newArray.splice(argumentsLength, newArray.length - argumentsLength);
    }
    setArgumentsValue([...newArray]);
    stepEntry.validatorStep.arguments = newArray;
    const newValue = recomposeSteps(validatorStepTableEntries);
    props.onValueChange(newValue);
  }


  function argumentValueOnChange(event: React.ChangeEvent<HTMLInputElement>, index: number) {
    const value = event.target.value;
    const newArray = [...argumentsValue];
    newArray[index] = value;
    setArgumentsValue(newArray);
    if (selectedIndex == 0) { return }
    const stepEntry = validatorStepTableEntries[selectedIndex-1];
    if (!stepEntry) { return }
    const argumentsLength = validatorInfo[stepEntry.validatorStep.type || ValidatorType.Grouping].arguments;
    while (newArray.length < argumentsLength) {
      newArray.push("");
    }
    if (newArray.length > argumentsLength) {
      newArray.splice(argumentsLength, newArray.length - argumentsLength);
    }
    stepEntry.validatorStep.arguments = newArray;
    const newValue = recomposeSteps(validatorStepTableEntries);
    props.onValueChange(newValue);
  }


  function operatorOnChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value as OperatorType;
    setOperatorValue(value);
    if (selectedIndex == 0) { return }
    const stepEntry = validatorStepTableEntries[selectedIndex-1];
    if (!stepEntry) { return }
    const children = stepEntry.validatorStep.and || stepEntry.validatorStep.or || [];
    if (value == OperatorType.And) {
      stepEntry.validatorStep.and = children;
      delete stepEntry.validatorStep.or;
    } else {
      stepEntry.validatorStep.or = children;
      delete stepEntry.validatorStep.and;
    }
    const newValue = recomposeSteps(validatorStepTableEntries);
    props.onValueChange(newValue);
  }


  function notOnChange(value: boolean) {
    setNotValue(value);
    if (selectedIndex == 0) { return }
    const step = validatorStepTableEntries[selectedIndex-1];
    if (!step) { return }
    step.validatorStep.not = value;
    const newValue = recomposeSteps(validatorStepTableEntries);
    props.onValueChange(newValue);
  }


  function addNewStep() {
    // The only attribute we can definitely set is the arguments.  Even the type may be undefined.
    const newStep: ValidatorStep = {
      arguments: [ ...argumentsValue],
    };
    // Grouping is the assumed default, so we only set the type if it's something else.
    if (typeValue != ValidatorType.Grouping) {
      newStep.type = typeValue;
    }
    if (operatorValue == OperatorType.And) {
      newStep.and = [];
    } else {
      newStep.or = [];
    }
    if (notValue) { newStep.not = true; }
    const newStepEntry: ValidationStepTableEntry = {
      indent: 0,
      validatorStep: newStep
    }
    if (validatorStepTableEntries.length < 2) {
      newStepEntry.indent = validatorStepTableEntries.length;
      validatorStepTableEntries.push(newStepEntry);
      setSelectedIndex(validatorStepTableEntries.length);
    } else if (selectedIndex > 0) {
      const selectedStep = validatorStepTableEntries[selectedIndex-1];
      if (selectedStep) {
        newStepEntry.indent = selectedStep.indent + 1;
        var insertIndex = selectedIndex;
        while (insertIndex < validatorStepTableEntries.length && validatorStepTableEntries[insertIndex].indent > selectedStep.indent) {
          insertIndex++;
        }
        validatorStepTableEntries.splice(insertIndex, 0, newStepEntry);
        setSelectedIndex(insertIndex+1);
      }
    } else {
      newStepEntry.indent = validatorStepTableEntries[validatorStepTableEntries.length-1].indent;
      validatorStepTableEntries.push(newStepEntry);
      setSelectedIndex(validatorStepTableEntries.length);
    }
    const newValue = recomposeSteps(validatorStepTableEntries);
    props.onValueChange(newValue);
  }


  function deleteSelectedIndex(index: number) {
    if (selectedIndex == 0) { return }
    const stepEntry = validatorStepTableEntries[selectedIndex-1];
    if (!stepEntry) { return }
    const currentIndent = stepEntry.indent;
    var stopIndex = index;
    while (stopIndex < validatorStepTableEntries.length && validatorStepTableEntries[stopIndex].indent > currentIndent) {
      stopIndex++;
    }
    const newArray = [...validatorStepTableEntries.slice(0, index-1), ...validatorStepTableEntries.slice(stopIndex)];
    const newValue = recomposeSteps(newArray);
    props.onValueChange(newValue);
    setSelectedIndex(0);
  }


  return (
    <div className="block">
      <table className="sortabletable striped">
        <thead>
          <tr>
            <th style={{padding: ".375rem 1rem .375rem 0.5rem", minWidth: "8rem", borderBottom: "1px solid #ddd" }}>Type</th>
            <th style={{padding: ".375rem 1rem .375rem 0.5rem", minWidth: "8rem", borderBottom: "1px solid #ddd" }}>Arguments</th>
            <th style={{padding: ".375rem 1rem .375rem 0.5rem", borderBottom: "1px solid #ddd" }}>Operator</th>
            <th style={{padding: ".375rem 1rem .375rem 0.5rem", borderBottom: "1px solid #ddd" }}></th>
          </tr>
        </thead>
        <tbody>
          { validatorStepTableEntries.map((stepEntry, index) => {
            return (<tr key={index} style={ index+1 == selectedIndex ? {
                color: "rgba(7, 73, 251, 1)",
                backgroundColor: "rgba(90, 105, 255, 0.2)"
              } : {} }>
                <td style={{padding: `0 1rem 0 ${stepEntry.indent+0.5}rem`, fontWeight: "bold", whiteSpace: "nowrap" }} onClick={() => selectIndex(index+1)}>
                  <div style={{display: "flex", gap: "0", alignItems: "center"}}>
                    <div>
                      <span className="icon">
                        <FontAwesomeIcon style={{ transform: "rotate(315deg)" }} size="xl" icon={faAngleLeft} />
                      </span>
                    </div>
                    <div style={{padding: `1rem 1rem 0.5rem 0`}}>
                      { validatorInfo[stepEntry.validatorStep.type || ValidatorType.Grouping].displayName }
                    </div>
                  </div>
                </td>
                <td style={{padding: "1rem 1rem 0 0"}} onClick={() => selectIndex(index+1)}>
                  { stepEntry.validatorStep.arguments ? stepEntry.validatorStep.arguments.join(", ") : "" }
                </td>
                <td style={{padding: "1rem 1rem 0 1rem"}} onClick={() => selectIndex(index+1)}>
                  { (!stepEntry.validatorStep.type || (stepEntry.validatorStep.type === ValidatorType.CommaList)) ? (
                    Object.hasOwn(stepEntry.validatorStep, "or") ? "OR" : Object.hasOwn(stepEntry.validatorStep, "and") ? "AND" : ""
                  ) : (
                    stepEntry.validatorStep.not ? "NOT" : ""
                  )}
                </td>
                <td style={{padding: ".5rem 1rem 0.5rem 0.5rem"}}>
                  <button className="button is-small" onClick={() => deleteSelectedIndex(index+1)}> - </button>
                </td>
              </tr>);
          })}
          <tr style={{background: "none"}}>
            <td style={{padding: "1.5rem 0 0 0"}}>
              <div className="select">
                <select onChange={typeOnChange}>{
                  validatorDisplayOrder.map((validatorType) => (
                    <option
                      key={validatorType}
                      value={validatorType}
                      selected={validatorType === typeValue}>{validatorInfo[validatorType].displayName}</option>
                  ))
                }
                </select>
              </div>
            </td>
            <td style={{padding: "1.5rem .5rem 0 1rem"}}>{
              argumentsValue.map((argValue, index) => (
                <input className="input" key={index} type="text" value={argValue} onChange={(e) => argumentValueOnChange(e, index)} />
              ))
            }
            </td>
            <td style={{padding: "1.5rem .5rem 0 0.5rem"}}>
              { (typeValue == ValidatorType.Grouping || typeValue == ValidatorType.CommaList) ? (
                <div className="select">
                  <select onChange={operatorOnChange}>
                    <option key={OperatorType.And} value={OperatorType.And}
                      selected={operatorValue === OperatorType.And}>{OperatorType.And}</option>
                    <option key={OperatorType.Or} value={OperatorType.Or}
                      selected={operatorValue === OperatorType.Or}>{OperatorType.Or}</option>
                  </select>
                </div>
              ) : (
                <button className="button" style={{ borderColor: "rgba(0,0,0,0)" }}
                  onClick={() => notOnChange(!notValue)}
                >
                  <span className="icon">
                    <FontAwesomeIcon icon={notValue ? faCheckSquare : faSquare} />
                  </span>
                  <span>Not</span>
                </button>
              )}
            </td>
            <td style={{padding: "1.5rem 0 0 0"}}>
              <button className="button" onClick={() => addNewStep()}> + </button>
            </td>
          </tr>

        </tbody>
      </table>

      <div style={{marginTop: "1rem", padding: "0 0.5rem"}}>{
        props.value ? `"${validatorStepToDescription(props.value)}"` : ""
        }
      </div>
    </div>
  )
}
export default ValidationCellModalContent
